import {
  DocumentTemplate,
  DocumentTemplateCategory,
  DocumentTemplatePrefilled,
} from 'generated/api';
import { api } from 'careand-redux/api/ApiThunks';
import { createResettableSlice } from 'careand-redux/utils/makeResettable';
import { unwrapApi } from 'careand-redux/utils/unwrap';
import PerformanceUtils from 'services/PerformanceUtils';
import _ from 'lodash';

export interface TemplatesState {
  templates: DocumentTemplate[];
  templatesPrefilled: DocumentTemplatePrefilled[];
  templateCategories: DocumentTemplateCategory[];
}

const initialState: TemplatesState = {
  templates: [],
  templatesPrefilled: [],
  templateCategories: [],
};

const slice = createResettableSlice({
  name: 'Templates',
  initialState,
  reducers: {
    // Socket-pushed AI-detection lifecycle update: merge the lite payload into
    // the stored entry (a full replace would wipe fields on a loaded template).
    receiveTemplateDetectionUpdate: (state, action) => {
      const update = action.payload as Partial<DocumentTemplate> & { id: string };
      const existing = state.templates.find((t) => t.id === update.id);
      const merged = existing ? { ...existing, ...update } : (update as DocumentTemplate);
      if (update.detection_status === null) delete (merged as any).detection_status;
      state.templates = sortTemplates(PerformanceUtils.upsert(merged, state.templates));
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(
        api.Templates.getDocumentTemplateCategories.fulfilled,
        unwrapApi((state, categories) => {
          const sorted = PerformanceUtils.sortBy(categories, ['name'], false);
          state.templateCategories = sorted;
        }),
      )
      .addCase(
        api.Templates.addDocumentTemplateCategory.fulfilled,
        unwrapApi((state, category) => {
          const updated = PerformanceUtils.upsert(category, state.templateCategories);
          state.templateCategories = PerformanceUtils.sortBy(updated, ['name'], false);
        }),
      )
      .addCase(
        api.Templates.getDocumentTemplates.fulfilled,
        unwrapApi((state, templates) => {
          state.templates = sortTemplates(templates);
        }),
      )
      .addCase(
        api.Templates.getDocumentTemplatesPrefilled.fulfilled,
        unwrapApi((state, templates) => {
          state.templatesPrefilled = templates;
        }),
      )
      .addCase(
        api.Templates.addDocumentTemplatePrefilled.fulfilled,
        unwrapApi((state, template) => {
          state.templatesPrefilled = PerformanceUtils.upsert(template, state.templatesPrefilled);
        }),
      )
      .addCase(
        api.Templates.deleteDocumentTemplatesPrefilled.fulfilled,
        unwrapApi((state, payload, req) => {
          PerformanceUtils.removeFromArrayInPlace({ id: req.id }, state.templatesPrefilled);
        }),
      )
      .addCase(api.Templates.getDocumentTemplate.fulfilled, unwrapApi(insertTemplate))
      .addCase(api.Templates.addDocumentTemplate.fulfilled, unwrapApi(insertTemplate))
      .addCase(api.Templates.updateDocumentTemplate.fulfilled, unwrapApi(insertTemplate))
      .addCase(
        api.Templates.deleteDocumentTemplate.fulfilled,
        unwrapApi((state, payload, req) => {
          PerformanceUtils.removeFromArrayInPlace({ id: req.id }, state.templates);
        }),
      )
      .addCase(api.Templates.addDocumentTemplateRequest.fulfilled, unwrapApi(insertTemplate)),
});

function insertTemplate(state: TemplatesState, template: DocumentTemplate) {
  state.templates = sortTemplates(PerformanceUtils.upsert(template, state.templates));
}

function sortTemplates(templates: DocumentTemplate[]) {
  function includes(templates: DocumentTemplate[], search: string, condition = true) {
    search = search.toLowerCase();
    return templates.filter(
      (template) => template.description?.toLowerCase().includes(search) === condition,
    );
  }

  let sorted = PerformanceUtils.sortBy(templates, ['description', 'secondary_name'], false);

  //nipissing
  let month = includes(sorted, 'month');
  month.sort((template1, template2) => {
    let split1: string[] = template1.description?.split(' ') || [];
    let split2: string[] = template2.description?.split(' ') || [];
    return Number.parseInt(split1[0]!) - Number.parseInt(split2[0]!);
  });

  sorted = includes(sorted, 'month', false);

  let year = includes(sorted, 'years old');
  sorted = includes(sorted, 'years old', false);

  //greig
  let ages = _.reverse(includes(sorted, 'ages '));
  sorted = includes(sorted, 'ages ', false);

  return [...month, ...year, ...ages, ...sorted];
}

export const templateActions = slice.actions;
export default slice.reducer;
